import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Grid,
  Icon,
  Label,
  Modal,
  Progress,
  Segment,
  Table,
} from 'semantic-ui-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { API, showError, showSuccess, timestamp2string } from '../../helpers';
import { CHANNEL_OPTIONS } from '../../constants';
import './Operator.css';

const emptyPlan = {
  name: '',
  billing_mode: 'monthly',
  period: 'month',
  quota: 1000000,
  price: 99,
  unit_price: 0.000099,
  alert_thresholds: '80,90,100',
  description: '',
  status: 'enabled',
};

const emptyPaymentSetting = {
  enabled: false,
  provider: 'manual',
  name: '线下转账',
  payment_url: '',
  merchant_id: '',
  app_id: '',
  secret: '',
  secret_set: false,
  secret_clear: false,
  notify_url: '',
  return_url: '',
  extra_params: '',
  quick_amounts: '50,100,300,500',
  min_amount: 1,
  max_amount: 99999,
  quota_per_unit: 1000000 / 92,
};

const billingOptions = [
  { key: 'volume', value: 'volume', text: '按量包' },
  { key: 'monthly', value: 'monthly', text: '月度套餐' },
  { key: 'yearly', value: 'yearly', text: '年度套餐' },
  { key: 'times', value: 'times', text: '按次套餐' },
];

const periodOptions = [
  { key: 'once', value: 'once', text: '一次性' },
  { key: 'month', value: 'month', text: '按月' },
  { key: 'year', value: 'year', text: '按年' },
];

const statusOptions = [
  { key: 'enabled', value: 'enabled', text: '启用' },
  { key: 'disabled', value: 'disabled', text: '停用' },
];

const paymentProviderOptions = [
  { key: 'manual', value: 'manual', text: '线下/手动确认' },
  { key: 'alipay', value: 'alipay', text: '支付宝' },
  { key: 'wechat', value: 'wechat', text: '微信支付' },
  { key: 'epay', value: 'epay', text: '易支付' },
  { key: 'custom', value: 'custom', text: '自定义网关' },
];

const videoTaskStatusOptions = [
  { key: 'all', value: 'all', text: '全部任务' },
  { key: 'queued', value: 'queued', text: '排队中' },
  { key: 'processing', value: 'processing', text: '生成中' },
  { key: 'succeeded', value: 'succeeded', text: '成功' },
  { key: 'failed', value: 'failed', text: '失败' },
  { key: 'timeout', value: 'timeout', text: '超时' },
  { key: 'refunded', value: 'refunded', text: '已退款' },
];

const videoBillingEventOptions = [
  { key: 'all', value: 'all', text: '全部流水' },
  { key: 'preconsume', value: 'preconsume', text: '预扣' },
  { key: 'final', value: 'final', text: '结算' },
  { key: 'refund', value: 'refund', text: '退款' },
  { key: 'adjust', value: 'adjust', text: '调整' },
];

const toneClass = {
  blue: 'operator-tone-blue',
  green: 'operator-tone-green',
  purple: 'operator-tone-purple',
  orange: 'operator-tone-orange',
  teal: 'operator-tone-teal',
  red: 'operator-tone-red',
};

function formatCompactNumber(value, digits = 0) {
  if (value === undefined || value === null) return '0';
  return new Intl.NumberFormat('zh-CN', {
    maximumFractionDigits: digits,
  }).format(value);
}

function formatQuota(value) {
  const quota = Number(value || 0);
  if (quota >= 100000000) return `${formatCompactNumber(quota / 100000000, 2)} 亿`;
  if (quota >= 10000) return `${formatCompactNumber(quota / 10000, 2)} 万`;
  return formatCompactNumber(quota);
}

function formatCurrency(value) {
  const amount = Number(value || 0);
  const absAmount = Math.abs(amount);
  let digits = 2;
  if (absAmount > 0 && absAmount < 0.0001) {
    digits = 8;
  } else if (absAmount > 0 && absAmount < 0.01) {
    digits = 6;
  }
  return `¥${formatCompactNumber(amount, digits)}`;
}

function formatBalanceAmount(quota, quotaPerUnit) {
  const unit = Number(localStorage.getItem('quota_per_unit') || quotaPerUnit || 1000000 / 92);
  return formatCurrency(unit > 0 ? Number(quota || 0) / unit : 0);
}

function getChannelTypeLabel(type) {
  const option = CHANNEL_OPTIONS.find((item) => item.value === type);
  return option ? option.text : `类型 ${type || '-'}`;
}

function renderAlertLabel(level) {
  switch (level) {
    case 'exhausted':
      return <Label color='red'>已用尽</Label>;
    case 'critical':
      return <Label color='orange'>高危</Label>;
    case 'warning':
      return <Label color='yellow'>预警</Label>;
    case 'frozen':
      return <Label color='grey'>冻结</Label>;
    default:
      return <Label color='green'>正常</Label>;
  }
}

function renderChannelStatus(status) {
  if (status === 1) return <Label color='green'>可用</Label>;
  if (status === 3) return <Label color='orange'>自动禁用</Label>;
  return <Label color='red'>停用</Label>;
}

function renderTopupStatus(status) {
  if (status === 'paying') return <Label color='blue'>支付中</Label>;
  if (status === 'paid') return <Label color='green'>已入账</Label>;
  if (status === 'failed') return <Label color='red'>支付失败</Label>;
  if (status === 'canceled') return <Label color='grey'>已取消</Label>;
  if (status === 'refunded') return <Label color='violet'>已退款</Label>;
  return <Label color='orange'>待支付</Label>;
}

function renderTransactionType(type) {
  const labels = {
    topup_order: '订单入账',
    redemption: '兑换码',
    admin_adjust: '人工调整',
    refund: '退款扣回',
  };
  return labels[type] || type || '-';
}

function renderVideoTaskStatus(status) {
  const map = {
    queued: { color: 'blue', text: '排队中' },
    processing: { color: 'yellow', text: '生成中' },
    succeeded: { color: 'green', text: '成功' },
    failed: { color: 'red', text: '失败' },
    timeout: { color: 'orange', text: '超时' },
    refunded: { color: 'violet', text: '已退款' },
  };
  const item = map[status] || { color: 'grey', text: status || '-' };
  return <Label color={item.color}>{item.text}</Label>;
}

function renderVideoBillingEvent(type) {
  const map = {
    preconsume: { color: 'blue', text: '预扣' },
    final: { color: 'green', text: '结算' },
    refund: { color: 'violet', text: '退款' },
    adjust: { color: 'orange', text: '调整' },
  };
  const item = map[type] || { color: 'grey', text: type || '-' };
  return <Label basic color={item.color}>{item.text}</Label>;
}

function formatVideoElapsed(task) {
  if (!task?.started_time) return '-';
  const end = task.completed_time || Math.floor(Date.now() / 1000);
  const seconds = Math.max(0, end - task.started_time);
  if (seconds >= 60) return `${Math.floor(seconds / 60)}分${seconds % 60}秒`;
  return `${seconds}秒`;
}

function isCurrencyMetric(key) {
  return [
    'month_revenue',
    'today_revenue',
    'month_topup_revenue',
    'month_cost',
    'month_gross_margin',
  ].includes(key);
}

function formatPercent(value) {
  return `${(Number(value || 0) * 100).toFixed(1)}%`;
}

function renderBalanceStatus(status) {
  const map = {
    ok: { color: 'green', text: '余额正常' },
    low: { color: 'orange', text: '余额偏低' },
    empty: { color: 'red', text: '余额耗尽' },
    stale: { color: 'yellow', text: '余额过期' },
    unknown: { color: 'grey', text: '未查询' },
  };
  const item = map[status] || map.unknown;
  return <Label color={item.color}>{item.text}</Label>;
}

function renderCircuitStatus(item) {
  if (item.circuit_open) {
    return <Label color='red'>熔断中</Label>;
  }
  if (item.consecutive_failures > 0) {
    return <Label color='orange'>观察中</Label>;
  }
  return <Label color='green'>正常</Label>;
}

const channelErrorTypeMap = {
  success: { color: 'green', text: '成功' },
  rate_limit: { color: 'orange', text: '限流' },
  server_error: { color: 'red', text: '上游 5xx' },
  network_error: { color: 'violet', text: '网络' },
  timeout: { color: 'purple', text: '超时' },
  client_error: { color: 'grey', text: '客户端' },
  local_error: { color: 'grey', text: '本地' },
  upstream_status: { color: 'yellow', text: '上游状态' },
  other: { color: 'grey', text: '其他' },
};

function renderChannelErrorType(type) {
  if (!type) return <Label basic>暂无</Label>;
  const item = channelErrorTypeMap[type] || channelErrorTypeMap.other;
  return <Label basic color={item.color}>{item.text}</Label>;
}

function renderFailureBreakdown(item) {
  const failures = [
    ['限流', item.rate_limited_count],
    ['5xx', item.server_error_count],
    ['网络', item.network_error_count],
    ['超时', item.timeout_count],
    ['4xx/本地', item.client_error_count],
    ['其他', item.other_error_count],
  ].filter(([, value]) => Number(value || 0) > 0);
  if (failures.length === 0) {
    return <span className='operator-muted'>暂无失败</span>;
  }
  return (
    <div className='operator-failure-tags'>
      {failures.slice(0, 3).map(([label, value]) => (
        <span key={label}>{label} {formatCompactNumber(value)}</span>
      ))}
    </div>
  );
}

const Operator = () => {
  const [overview, setOverview] = useState(null);
  const [paymentSetting, setPaymentSetting] = useState(emptyPaymentSetting);
  const [topupOrders, setTopupOrders] = useState([]);
  const [billingTransactions, setBillingTransactions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [videoTasks, setVideoTasks] = useState([]);
  const [videoBillingRecords, setVideoBillingRecords] = useState([]);
  const [videoTaskStatus, setVideoTaskStatus] = useState('all');
  const [videoBillingEvent, setVideoBillingEvent] = useState('all');
  const [loading, setLoading] = useState(true);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(emptyPlan);
  const [savingPlan, setSavingPlan] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        paymentRes,
        ordersRes,
        transactionsRes,
        auditRes,
        videoTasksRes,
        videoBillingRes,
      ] = await Promise.all([
        API.get('/api/operator/overview'),
        API.get('/api/topup/admin/setting'),
        API.get('/api/topup/admin/orders?p=0'),
        API.get('/api/billing/admin/transactions?p=0'),
        API.get('/api/audit/admin/logs?p=0'),
        API.get('/api/video-generation/tasks?p=0&scope=all'),
        API.get('/api/video-generation/billing-records?p=0&scope=all'),
      ]);

      if (overviewRes.data.success) {
        setOverview(overviewRes.data.data);
      } else {
        showError(overviewRes.data.message);
      }
      if (paymentRes.data.success) {
        setPaymentSetting({ ...emptyPaymentSetting, ...paymentRes.data.data, secret: '' });
      } else {
        showError(paymentRes.data.message);
      }
      if (ordersRes.data.success) {
        setTopupOrders(ordersRes.data.data || []);
      } else {
        showError(ordersRes.data.message);
      }
      if (transactionsRes.data.success) {
        setBillingTransactions(transactionsRes.data.data || []);
      } else {
        showError(transactionsRes.data.message);
      }
      if (auditRes.data.success) {
        setAuditLogs(auditRes.data.data || []);
      } else {
        showError(auditRes.data.message);
      }
      if (videoTasksRes.data.success) {
        setVideoTasks(videoTasksRes.data.data || []);
      } else {
        showError(videoTasksRes.data.message);
      }
      if (videoBillingRes.data.success) {
        setVideoBillingRecords(videoBillingRes.data.data || []);
      } else {
        showError(videoBillingRes.data.message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverview().catch(showError);
  }, []);

  const chartData = useMemo(() => {
    return (overview?.daily_usage || []).map((item) => ({
      day: item.day.slice(5),
      tokens: item.tokens,
      requests: item.request_count,
      quota: item.quota,
    }));
  }, [overview]);

  const filteredVideoTasks = useMemo(() => {
    if (videoTaskStatus === 'all') return videoTasks;
    return videoTasks.filter((task) => task.status === videoTaskStatus);
  }, [videoTasks, videoTaskStatus]);

  const filteredVideoBillingRecords = useMemo(() => {
    if (videoBillingEvent === 'all') return videoBillingRecords;
    return videoBillingRecords.filter((record) => record.event_type === videoBillingEvent);
  }, [videoBillingRecords, videoBillingEvent]);

  const openCreatePlan = () => {
    setEditingPlan(emptyPlan);
    setPlanModalOpen(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan({ ...plan });
    setPlanModalOpen(true);
  };

  const updatePlanField = (field, value) => {
    setEditingPlan((plan) => ({ ...plan, [field]: value }));
  };

  const updatePaymentField = (field, value) => {
    setPaymentSetting((setting) => ({ ...setting, [field]: value }));
  };

  const savePlan = async () => {
    if (!editingPlan.name.trim()) {
      showError('请填写套餐名称');
      return;
    }
    setSavingPlan(true);
    const payload = {
      ...editingPlan,
      quota: Number(editingPlan.quota || 0),
      price: Number(editingPlan.price || 0),
      unit_price: Number(editingPlan.unit_price || 0),
    };
    const res = payload.id
      ? await API.put(`/api/operator/plans/${payload.id}`, payload)
      : await API.post('/api/operator/plans', payload);
    const { success, message } = res.data;
    if (success) {
      showSuccess(payload.id ? '套餐已更新' : '套餐已创建');
      setPlanModalOpen(false);
      await loadOverview();
    } else {
      showError(message);
    }
    setSavingPlan(false);
  };

  const savePaymentSetting = async () => {
    setSavingPayment(true);
    const payload = {
      ...paymentSetting,
      min_amount: Number(paymentSetting.min_amount || 0),
      max_amount: Number(paymentSetting.max_amount || 0),
      quota_per_unit: Number(paymentSetting.quota_per_unit || 0),
    };
    const res = await API.put('/api/topup/admin/setting', payload);
    const { success, message, data } = res.data;
    if (success) {
      showSuccess('支付配置已保存');
      setPaymentSetting({ ...emptyPaymentSetting, ...data, secret: '' });
    } else {
      showError(message);
    }
    setSavingPayment(false);
  };

  const confirmTopupOrder = async (order) => {
    const tradeNo = window.prompt(`请输入订单「${order.order_no}」的支付交易号，可留空`);
    if (tradeNo === null) return;
    const res = await API.post(`/api/topup/admin/orders/${order.id}/confirm`, {
      trade_no: tradeNo,
      remark: '后台确认支付',
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess('充值订单已确认入账');
      await loadOverview();
    } else {
      showError(message);
    }
  };

  const cancelTopupOrder = async (order) => {
    const remark = window.prompt(`请输入取消订单「${order.order_no}」的原因`, '后台取消');
    if (remark === null) return;
    const res = await API.post(`/api/topup/admin/orders/${order.id}/cancel`, {
      remark,
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess('充值订单已取消');
      await loadOverview();
    } else {
      showError(message);
    }
  };

  const failTopupOrder = async (order) => {
    const remark = window.prompt(`请输入标记订单「${order.order_no}」失败的原因`, '支付失败');
    if (remark === null) return;
    const res = await API.post(`/api/topup/admin/orders/${order.id}/fail`, {
      remark,
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess('订单已标记为支付失败');
      await loadOverview();
    } else {
      showError(message);
    }
  };

  const refundTopupOrder = async (order) => {
    const remark = window.prompt(`确认退款订单「${order.order_no}」并扣回余额？请输入备注`, '后台退款');
    if (remark === null) return;
    const res = await API.post(`/api/topup/admin/orders/${order.id}/refund`, {
      remark,
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess('充值订单已退款并扣回余额');
      await loadOverview();
    } else {
      showError(message);
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(`确认删除套餐「${plan.name}」？`)) return;
    const res = await API.delete(`/api/operator/plans/${plan.id}`);
    const { success, message } = res.data;
    if (success) {
      showSuccess('套餐已删除');
      await loadOverview();
    } else {
      showError(message);
    }
  };

  return (
    <div className='operator-shell'>
      <section className='operator-hero'>
        <div>
          <Label className='operator-eyebrow'>运营商 Token 中转站</Label>
          <h1>统一调度、计量、计费与预警</h1>
          <p>
            面向企业客户的 Token 中转运营台，集中查看客户资产、上游通路、API
            密钥和套餐模板，让资源消耗与经营数据保持透明。
          </p>
        </div>
        <div className='operator-hero-actions'>
          <Button icon labelPosition='left' onClick={loadOverview} loading={loading}>
            <Icon name='refresh' />
            刷新数据
          </Button>
          <Button primary icon labelPosition='left' onClick={openCreatePlan}>
            <Icon name='plus' />
            新建套餐
          </Button>
        </div>
      </section>

      <section className='operator-metrics'>
        {(overview?.metrics || []).map((metric) => (
          <div
            className={`operator-metric ${toneClass[metric.tone] || ''}`}
            key={metric.key}
          >
            <div className='operator-metric-icon'>
              <Icon name={metric.icon} />
            </div>
            <div>
              <span>{metric.label}</span>
              <strong>
                {isCurrencyMetric(metric.key)
                  ? formatCurrency(metric.value)
                  : formatCompactNumber(metric.value)}
                {!isCurrencyMetric(metric.key) && (
                  <em>{metric.unit}</em>
                )}
              </strong>
              <small>{metric.trend}</small>
            </div>
          </div>
        ))}
      </section>

      <Grid stackable columns={2} className='operator-grid'>
        <Grid.Column width={10}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>通道健康看板</h2>
                <p>健康分综合成功率、平均延迟、连续失败、余额状态和熔断状态。</p>
              </div>
              <Label basic>
                成本率 {(overview?.business?.cost_rate * 100 || 0).toFixed(0)}%
              </Label>
            </div>
            <Table basic='very' className='operator-table operator-health-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>通道</Table.HeaderCell>
                  <Table.HeaderCell>策略</Table.HeaderCell>
                  <Table.HeaderCell>健康分</Table.HeaderCell>
                  <Table.HeaderCell>成功率 / 延迟</Table.HeaderCell>
                  <Table.HeaderCell>失败类型</Table.HeaderCell>
                  <Table.HeaderCell>余额</Table.HeaderCell>
                  <Table.HeaderCell>熔断</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(overview?.channel_health || []).map((item) => (
                  <Table.Row key={item.channel_id}>
                    <Table.Cell>
                      <strong>{item.channel_name || `通道 #${item.channel_id}`}</strong>
                      <span className='operator-muted'>#{item.channel_id} {item.group}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={item.role === 'primary' ? 'blue' : 'grey'}>
                        {item.role === 'primary' ? '主通道' : '备用'}
                      </Label>
                      <span className='operator-muted'>
                        权重 {item.weight || 0} / 优先级 {item.priority || 0}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <Progress
                        percent={Math.round(Math.min(1, item.score || 0) * 100)}
                        size='tiny'
                        color={(item.score || 0) >= 0.8 ? 'green' : (item.score || 0) >= 0.5 ? 'yellow' : 'red'}
                      />
                      <span className='operator-muted'>{formatPercent(Math.min(1, item.score || 0))}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{formatPercent(item.success_rate)}</strong>
                      <span className='operator-muted'>
                        {Math.round(item.avg_latency_ms || 0)} ms · 连续失败 {item.consecutive_failures || 0}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      {renderChannelErrorType(item.last_error_type)}
                      {renderFailureBreakdown(item)}
                    </Table.Cell>
                    <Table.Cell>
                      {renderBalanceStatus(item.balance_status)}
                      <span className='operator-muted'>
                        {item.balance_updated_time ? `$${Number(item.balance || 0).toFixed(2)}` : '未同步余额'}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      {renderCircuitStatus(item)}
                      <span className='operator-muted'>
                        {item.last_status_code ? `HTTP ${item.last_status_code}` : '暂无调用'}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                ))}
                {(overview?.channel_health || []).length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='7' textAlign='center'>
                      暂无通道健康数据
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>

        <Grid.Column width={6}>
          <Segment className='operator-panel operator-reconcile-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>自动对账</h2>
                <p>校验支付订单、余额流水与用户当前余额的一致性。</p>
              </div>
              <Label color={overview?.reconciliation?.healthy ? 'green' : 'red'}>
                {overview?.reconciliation?.healthy ? '一致' : `${overview?.reconciliation?.issue_count || 0} 个异常`}
              </Label>
            </div>
            <div className='operator-reconcile-summary'>
              <div>
                <span>检查订单</span>
                <strong>{overview?.reconciliation?.checked_orders || 0}</strong>
              </div>
              <div>
                <span>检查用户</span>
                <strong>{overview?.reconciliation?.checked_users || 0}</strong>
              </div>
              <div>
                <span>异常数量</span>
                <strong>{overview?.reconciliation?.issue_count || 0}</strong>
              </div>
            </div>
            <div className='operator-reconcile-list'>
              {(overview?.reconciliation?.issues || []).slice(0, 5).map((issue, index) => (
                <div className='operator-reconcile-item' key={`${issue.type}-${index}`}>
                  <Label color={issue.severity === 'critical' ? 'red' : 'orange'}>
                    {issue.type}
                  </Label>
                  <strong>{issue.source_no || issue.username || `用户 #${issue.user_id}`}</strong>
                  <span>{issue.message}</span>
                  <small>期望 {formatQuota(issue.expected)} / 实际 {formatQuota(issue.actual)}</small>
                </div>
              ))}
              {(overview?.reconciliation?.issues || []).length === 0 && (
                <div className='operator-empty-state'>本次对账未发现异常</div>
              )}
            </div>
          </Segment>
        </Grid.Column>
      </Grid>

      <Grid stackable columns={2} className='operator-grid'>
        <Grid.Column width={10}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>视频任务中心</h2>
                <p>集中追踪 Seedance 任务的排队、生成、失败退款和下载状态。</p>
              </div>
              <div className='operator-panel-tools'>
                <Dropdown
                  compact
                  selection
                  options={videoTaskStatusOptions}
                  value={videoTaskStatus}
                  onChange={(_, { value }) => setVideoTaskStatus(value)}
                />
                <Label basic>{filteredVideoTasks.length} 条</Label>
              </div>
            </div>
            <Table basic='very' className='operator-table operator-video-task-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>任务</Table.HeaderCell>
                  <Table.HeaderCell>令牌</Table.HeaderCell>
                  <Table.HeaderCell>模型</Table.HeaderCell>
                  <Table.HeaderCell>场景</Table.HeaderCell>
                  <Table.HeaderCell>扣费</Table.HeaderCell>
                  <Table.HeaderCell>状态</Table.HeaderCell>
                  <Table.HeaderCell>下载</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredVideoTasks.map((task) => (
                  <Table.Row key={task.task_id}>
                    <Table.Cell>
                      <strong>{task.task_id}</strong>
                      <span className='operator-muted'>
                        提交 {timestamp2string(task.submitted_time)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{task.token_name || '-'}</strong>
                      <span className='operator-muted'>用户 #{task.user_id} / Token #{task.token_id}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{task.model_name}</strong>
                      <span className='operator-muted'>{task.ratio || '-'} · {String(task.resolution || '').toUpperCase()}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <Label basic color={task.has_video_input ? 'teal' : 'grey'}>
                        {task.has_video_input ? '含视频输入' : '无视频输入'}
                      </Label>
                      <span className='operator-muted'>{task.duration || 0} 秒</span>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{formatCurrency(task.amount_rmb)}</strong>
                      <span className='operator-muted'>
                        资源 Tokens {formatQuota(task.resource_tokens)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      {renderVideoTaskStatus(task.status)}
                      <span className='operator-muted'>耗时 {formatVideoElapsed(task)}</span>
                    </Table.Cell>
                    <Table.Cell>
                      {task.local_video_url ? (
                        <a
                          className='operator-video-link'
                          href={task.local_video_url}
                          target='_blank'
                          rel='noreferrer'
                        >
                          下载视频
                        </a>
                      ) : (
                        <span className='operator-muted'>
                          {task.error_message || '未生成文件'}
                        </span>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
                {filteredVideoTasks.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='7' textAlign='center'>
                      暂无视频任务
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>

        <Grid.Column width={6}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>视频账单流水</h2>
                <p>展示每次生成的预扣、结算调整与失败退款记录。</p>
              </div>
              <div className='operator-panel-tools'>
                <Dropdown
                  compact
                  selection
                  options={videoBillingEventOptions}
                  value={videoBillingEvent}
                  onChange={(_, { value }) => setVideoBillingEvent(value)}
                />
                <Label basic>{filteredVideoBillingRecords.length} 条</Label>
              </div>
            </div>
            <Table basic='very' className='operator-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>任务</Table.HeaderCell>
                  <Table.HeaderCell>事件</Table.HeaderCell>
                  <Table.HeaderCell>金额</Table.HeaderCell>
                  <Table.HeaderCell>资源 Tokens</Table.HeaderCell>
                  <Table.HeaderCell>时间</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredVideoBillingRecords.map((record) => (
                  <Table.Row key={record.id}>
                    <Table.Cell>
                      <strong>{record.task_id}</strong>
                      <span className='operator-muted'>{record.model_name}</span>
                    </Table.Cell>
                    <Table.Cell>
                      {renderVideoBillingEvent(record.event_type)}
                      <span className='operator-muted'>
                        {record.input_scene} · {String(record.resolution || '').toUpperCase()}
                      </span>
                    </Table.Cell>
                    <Table.Cell>{formatCurrency(record.amount_rmb)}</Table.Cell>
                    <Table.Cell>
                      <strong>{formatQuota(record.resource_tokens)}</strong>
                      <span className='operator-muted'>模型 Tokens {formatQuota(record.model_tokens)}</span>
                    </Table.Cell>
                    <Table.Cell>
                      {timestamp2string(record.created_time)}
                      <span className='operator-muted'>{record.remark || '-'}</span>
                    </Table.Cell>
                  </Table.Row>
                ))}
                {filteredVideoBillingRecords.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='5' textAlign='center'>
                      暂无视频账单流水
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>
      </Grid>

      <Grid stackable columns={2} className='operator-grid'>
        <Grid.Column width={8}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>模型收入排行</h2>
                <p>按本月调用消耗折算收入、成本和毛利。</p>
              </div>
            </div>
            <Table basic='very' className='operator-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>模型</Table.HeaderCell>
                  <Table.HeaderCell>请求</Table.HeaderCell>
                  <Table.HeaderCell>收入</Table.HeaderCell>
                  <Table.HeaderCell>成本</Table.HeaderCell>
                  <Table.HeaderCell>毛利</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(overview?.business?.model_rankings || []).map((item) => (
                  <Table.Row key={item.key}>
                    <Table.Cell>
                      <strong>{item.display_name || item.name}</strong>
                      <span className='operator-muted'>{formatQuota(item.token_count)} Token</span>
                    </Table.Cell>
                    <Table.Cell>{formatCompactNumber(item.request_count)}</Table.Cell>
                    <Table.Cell>{formatCurrency(item.revenue)}</Table.Cell>
                    <Table.Cell>{formatCurrency(item.cost)}</Table.Cell>
                    <Table.Cell>
                      <strong>{formatCurrency(item.gross_margin)}</strong>
                      <span className='operator-muted'>{formatPercent(item.gross_margin_rate)}</span>
                    </Table.Cell>
                  </Table.Row>
                ))}
                {(overview?.business?.model_rankings || []).length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='5' textAlign='center'>暂无模型收入数据</Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>

        <Grid.Column width={8}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>客户收入排行</h2>
                <p>按客户调用消耗折算经营贡献。</p>
              </div>
            </div>
            <Table basic='very' className='operator-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>客户</Table.HeaderCell>
                  <Table.HeaderCell>请求</Table.HeaderCell>
                  <Table.HeaderCell>收入</Table.HeaderCell>
                  <Table.HeaderCell>成本</Table.HeaderCell>
                  <Table.HeaderCell>毛利</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(overview?.business?.customer_rankings || []).map((item) => (
                  <Table.Row key={item.key}>
                    <Table.Cell>
                      <strong>{item.display_name || item.name}</strong>
                      <span className='operator-muted'>{formatQuota(item.token_count)} Token</span>
                    </Table.Cell>
                    <Table.Cell>{formatCompactNumber(item.request_count)}</Table.Cell>
                    <Table.Cell>{formatCurrency(item.revenue)}</Table.Cell>
                    <Table.Cell>{formatCurrency(item.cost)}</Table.Cell>
                    <Table.Cell>
                      <strong>{formatCurrency(item.gross_margin)}</strong>
                      <span className='operator-muted'>{formatPercent(item.gross_margin_rate)}</span>
                    </Table.Cell>
                  </Table.Row>
                ))}
                {(overview?.business?.customer_rankings || []).length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='5' textAlign='center'>暂无客户收入数据</Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>
      </Grid>

      <Grid stackable columns={2} className='operator-grid'>
        <Grid.Column width={7}>
          <Segment className='operator-panel operator-payment-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>支付接口配置</h2>
                <p>配置用户在线充值网关，密钥会加密存储。</p>
              </div>
              <Label color={paymentSetting.enabled ? 'green' : 'grey'}>
                {paymentSetting.enabled ? '已启用' : '未启用'}
              </Label>
            </div>
            <Form className='operator-payment-form'>
              <Form.Checkbox
                toggle
                label='启用在线充值'
                checked={paymentSetting.enabled}
                onChange={(_, { checked }) => updatePaymentField('enabled', checked)}
              />
              <Form.Group widths='equal'>
                <Form.Field>
                  <label>支付渠道</label>
                  <Dropdown
                    selection
                    options={paymentProviderOptions}
                    value={paymentSetting.provider}
                    onChange={(_, { value }) => updatePaymentField('provider', value)}
                  />
                </Form.Field>
                <Form.Input
                  label='显示名称'
                  value={paymentSetting.name}
                  onChange={(_, { value }) => updatePaymentField('name', value)}
                />
              </Form.Group>
              <Form.Input
                label='支付网关地址'
                placeholder='https://pay.example.com/create'
                value={paymentSetting.payment_url}
                onChange={(_, { value }) => updatePaymentField('payment_url', value)}
              />
              <Form.Group widths='equal'>
                <Form.Input
                  label='商户号'
                  value={paymentSetting.merchant_id}
                  onChange={(_, { value }) => updatePaymentField('merchant_id', value)}
                />
                <Form.Input
                  label='应用 ID'
                  value={paymentSetting.app_id}
                  onChange={(_, { value }) => updatePaymentField('app_id', value)}
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  label='签名密钥'
                  type='password'
                  placeholder={paymentSetting.secret_set ? '已设置，留空不修改' : '请输入支付密钥'}
                  value={paymentSetting.secret}
                  onChange={(_, { value }) => updatePaymentField('secret', value)}
                />
                <Form.Checkbox
                  className='operator-secret-clear'
                  label='清除已保存密钥'
                  checked={paymentSetting.secret_clear}
                  onChange={(_, { checked }) => updatePaymentField('secret_clear', checked)}
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  label='回调地址'
                  value={paymentSetting.notify_url}
                  onChange={(_, { value }) => updatePaymentField('notify_url', value)}
                />
                <Form.Input
                  label='返回地址'
                  value={paymentSetting.return_url}
                  onChange={(_, { value }) => updatePaymentField('return_url', value)}
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  label='快捷金额'
                  value={paymentSetting.quick_amounts}
                  placeholder='50,100,300,500'
                  onChange={(_, { value }) => updatePaymentField('quick_amounts', value)}
                />
                <Form.Input
                  label='余额单位 / 元'
                  type='number'
                  placeholder='例如 10869.565，表示 1000000 配额 = ¥92'
                  value={paymentSetting.quota_per_unit}
                  onChange={(_, { value }) => updatePaymentField('quota_per_unit', value)}
                />
              </Form.Group>
              <Form.Group widths='equal'>
                <Form.Input
                  label='最小金额'
                  type='number'
                  value={paymentSetting.min_amount}
                  onChange={(_, { value }) => updatePaymentField('min_amount', value)}
                />
                <Form.Input
                  label='最大金额'
                  type='number'
                  value={paymentSetting.max_amount}
                  onChange={(_, { value }) => updatePaymentField('max_amount', value)}
                />
              </Form.Group>
              <Form.TextArea
                label='扩展参数 JSON'
                rows={3}
                placeholder='{"pay_type":"alipay"}'
                value={paymentSetting.extra_params}
                onChange={(_, { value }) => updatePaymentField('extra_params', value)}
              />
              <Button primary icon labelPosition='left' onClick={savePaymentSetting} loading={savingPayment}>
                <Icon name='save' />
                保存支付配置
              </Button>
            </Form>
          </Segment>
        </Grid.Column>

        <Grid.Column width={9}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>充值订单管理</h2>
                <p>确认用户支付后，订单金额会立即写入用户余额。</p>
              </div>
              <Button compact icon='refresh' onClick={loadOverview} loading={loading} />
            </div>
            <Table basic='very' className='operator-table operator-topup-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>订单</Table.HeaderCell>
                  <Table.HeaderCell>用户</Table.HeaderCell>
                  <Table.HeaderCell>金额</Table.HeaderCell>
                  <Table.HeaderCell>入账余额</Table.HeaderCell>
                  <Table.HeaderCell>状态</Table.HeaderCell>
                  <Table.HeaderCell>操作</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {topupOrders.map((order) => (
                  <Table.Row key={order.id}>
                    <Table.Cell>
                      <strong>{order.order_no}</strong>
                      <span className='operator-muted'>
                        {timestamp2string(order.created_time)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{order.username}</strong>
                      <span className='operator-muted'>#{order.user_id}</span>
                    </Table.Cell>
                    <Table.Cell>{formatCurrency(order.amount)}</Table.Cell>
                    <Table.Cell>{formatBalanceAmount(order.quota, paymentSetting.quota_per_unit)}</Table.Cell>
                    <Table.Cell>{renderTopupStatus(order.status)}</Table.Cell>
                    <Table.Cell>
                      {order.status === 'pending' || order.status === 'paying' ? (
                        <div className='operator-row-actions'>
                          <Button size='mini' primary onClick={() => confirmTopupOrder(order)}>
                            确认
                          </Button>
                          <Button size='mini' basic color='red' onClick={() => cancelTopupOrder(order)}>
                            取消
                          </Button>
                          <Button size='mini' basic color='orange' onClick={() => failTopupOrder(order)}>
                            失败
                          </Button>
                        </div>
                      ) : order.status === 'paid' ? (
                        <div className='operator-row-actions'>
                          <Button size='mini' basic color='violet' onClick={() => refundTopupOrder(order)}>
                            退款
                          </Button>
                          <span className='operator-muted'>
                            {order.operator || '-'}
                          </span>
                        </div>
                      ) : (
                        <span className='operator-muted'>
                          {order.operator || '-'}
                        </span>
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
                {topupOrders.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='6' textAlign='center'>
                      暂无充值订单
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>
      </Grid>

      <Grid stackable columns={2} className='operator-grid'>
        <Grid.Column width={10}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>7 日用量趋势</h2>
                <p>聚合所有下游客户的请求量、Token 消耗与实际消费。</p>
              </div>
              <Label basic>
                生成时间：
                {overview?.generated_time
                  ? timestamp2string(overview.generated_time)
                  : '-'}
              </Label>
            </div>
            <div className='operator-chart'>
              <ResponsiveContainer width='100%' height={260}>
                <AreaChart data={chartData} margin={{ top: 12, right: 18, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id='operatorTokens' x1='0' y1='0' x2='0' y2='1'>
                      <stop offset='5%' stopColor='#1f9d8a' stopOpacity={0.28} />
                      <stop offset='95%' stopColor='#1f9d8a' stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke='#e8edf2' vertical={false} />
                  <XAxis dataKey='day' tickLine={false} axisLine={false} />
                  <YAxis tickLine={false} axisLine={false} tickFormatter={formatQuota} />
                  <Tooltip
                    formatter={(value, name) => [
                      name === 'tokens' ? formatQuota(value) : formatCompactNumber(value),
                      name === 'tokens' ? 'Token' : '请求数',
                    ]}
                  />
                  <Area
                    type='monotone'
                    dataKey='tokens'
                    stroke='#1f9d8a'
                    strokeWidth={3}
                    fill='url(#operatorTokens)'
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Segment>
        </Grid.Column>

        <Grid.Column width={6}>
          <Segment className='operator-panel operator-plan-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>套餐模板</h2>
                <p>按量、月度、年度套餐统一配置。</p>
              </div>
              <Button compact icon='plus' onClick={openCreatePlan} />
            </div>
            <div className='operator-plan-list'>
              {(overview?.plans || []).map((plan) => (
                <div className='operator-plan' key={plan.id}>
                  <div>
                    <Label color={plan.status === 'enabled' ? 'green' : 'grey'}>
                      {plan.status === 'enabled' ? '启用' : '停用'}
                    </Label>
                    <h3>{plan.name}</h3>
                    <p>{plan.description || '暂无套餐说明'}</p>
                  </div>
                  <div className='operator-plan-meta'>
                    <strong>{formatCurrency(plan.price)}</strong>
                    <span>{formatQuota(plan.quota)} Token</span>
                  </div>
                  <div className='operator-plan-actions'>
                    <Button size='mini' basic onClick={() => openEditPlan(plan)}>
                      编辑
                    </Button>
                    <Button size='mini' basic color='red' onClick={() => deletePlan(plan)}>
                      删除
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Segment>
        </Grid.Column>
      </Grid>

      <Grid stackable columns={2} className='operator-grid'>
        <Grid.Column width={9}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>客户使用与预警</h2>
                <p>按已用金额排序，快速识别高消耗或即将用尽的客户。</p>
              </div>
            </div>
            <Table basic='very' className='operator-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>客户</Table.HeaderCell>
                  <Table.HeaderCell>分组</Table.HeaderCell>
                  <Table.HeaderCell>API 密钥</Table.HeaderCell>
                  <Table.HeaderCell>用量</Table.HeaderCell>
                  <Table.HeaderCell>状态</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(overview?.customers || []).map((customer) => (
                  <Table.Row key={customer.id}>
                    <Table.Cell>
                      <strong>{customer.display_name || customer.username}</strong>
                      <span className='operator-muted'>#{customer.id} {customer.username}</span>
                    </Table.Cell>
                    <Table.Cell>{customer.group}</Table.Cell>
                    <Table.Cell>{customer.token_count}</Table.Cell>
                    <Table.Cell>
                      <Progress
                        percent={Math.min(100, Math.round(customer.usage_rate || 0))}
                        size='tiny'
                        color={customer.usage_rate >= 90 ? 'red' : customer.usage_rate >= 80 ? 'yellow' : 'green'}
                      />
                      <span className='operator-muted'>
                        已用 {formatQuota(customer.used_quota)} / 总 {formatQuota(customer.total_quota)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>{renderAlertLabel(customer.alert_level)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>

        <Grid.Column width={7}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>上游运营商通路</h2>
                <p>展示可用通路、消耗、延迟与状态。</p>
              </div>
            </div>
            <Table basic='very' className='operator-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>通路</Table.HeaderCell>
                  <Table.HeaderCell>类型</Table.HeaderCell>
                  <Table.HeaderCell>Token</Table.HeaderCell>
                  <Table.HeaderCell>延迟</Table.HeaderCell>
                  <Table.HeaderCell>状态</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(overview?.channels || []).map((channel) => (
                  <Table.Row key={channel.channel_id}>
                    <Table.Cell>
                      <strong>{channel.channel_name}</strong>
                      <span className='operator-muted'>#{channel.channel_id} {channel.group}</span>
                    </Table.Cell>
                    <Table.Cell>{getChannelTypeLabel(channel.type)}</Table.Cell>
                    <Table.Cell>{formatQuota(channel.tokens)}</Table.Cell>
                    <Table.Cell>{formatCompactNumber(channel.avg_latency, 0)} ms</Table.Cell>
                    <Table.Cell>{renderChannelStatus(channel.status)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>
      </Grid>

      <Grid stackable columns={2} className='operator-grid'>
        <Grid.Column width={8}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>财务流水</h2>
                <p>记录充值入账、兑换码、人工调整和退款扣回。</p>
              </div>
              <Label basic>{billingTransactions.length} 条</Label>
            </div>
            <Table basic='very' className='operator-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>用户</Table.HeaderCell>
                  <Table.HeaderCell>类型</Table.HeaderCell>
                  <Table.HeaderCell>金额变动</Table.HeaderCell>
                  <Table.HeaderCell>余额</Table.HeaderCell>
                  <Table.HeaderCell>来源</Table.HeaderCell>
                  <Table.HeaderCell>时间</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {billingTransactions.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <strong>{item.username}</strong>
                      <span className='operator-muted'>#{item.user_id}</span>
                    </Table.Cell>
                    <Table.Cell>{renderTransactionType(item.type)}</Table.Cell>
                    <Table.Cell>
                      <Label color={Number(item.amount_rmb || 0) >= 0 ? 'green' : 'red'}>
                        {Number(item.amount_rmb || 0) >= 0 ? '+' : '-'}
                        {formatCurrency(Math.abs(Number(item.amount_rmb || 0)))}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>{formatCurrency(item.balance_after_rmb)}</Table.Cell>
                    <Table.Cell>
                      <strong>{item.source_no || '-'}</strong>
                      <span className='operator-muted'>{item.remark || '-'}</span>
                    </Table.Cell>
                    <Table.Cell>{timestamp2string(item.created_time)}</Table.Cell>
                  </Table.Row>
                ))}
                {billingTransactions.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='6' textAlign='center'>
                      暂无财务流水
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>

        <Grid.Column width={8}>
          <Segment className='operator-panel'>
            <div className='operator-panel-head'>
              <div>
                <h2>审计日志</h2>
                <p>追踪后台关键配置、充值订单和用户资产操作。</p>
              </div>
              <Label basic>{auditLogs.length} 条</Label>
            </div>
            <Table basic='very' className='operator-table'>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>操作者</Table.HeaderCell>
                  <Table.HeaderCell>动作</Table.HeaderCell>
                  <Table.HeaderCell>资源</Table.HeaderCell>
                  <Table.HeaderCell>来源 IP</Table.HeaderCell>
                  <Table.HeaderCell>时间</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {auditLogs.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <strong>{item.actor_name || '-'}</strong>
                      <span className='operator-muted'>#{item.actor_id || '-'}</span>
                    </Table.Cell>
                    <Table.Cell>{item.action}</Table.Cell>
                    <Table.Cell>
                      <strong>{item.resource_name || item.resource_id || '-'}</strong>
                      <span className='operator-muted'>{item.resource_type}</span>
                    </Table.Cell>
                    <Table.Cell>{item.ip || '-'}</Table.Cell>
                    <Table.Cell>{timestamp2string(item.created_time)}</Table.Cell>
                  </Table.Row>
                ))}
                {auditLogs.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan='5' textAlign='center'>
                      暂无审计日志
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>
      </Grid>

      <Modal
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        size='small'
      >
        <Modal.Header>{editingPlan.id ? '编辑套餐模板' : '新建套餐模板'}</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label='套餐名称'
              value={editingPlan.name}
              onChange={(_, { value }) => updatePlanField('name', value)}
              placeholder='例如：企业月度包'
            />
            <Form.Group widths='equal'>
              <Form.Field>
                <label>计费模式</label>
                <Dropdown
                  selection
                  options={billingOptions}
                  value={editingPlan.billing_mode}
                  onChange={(_, { value }) => updatePlanField('billing_mode', value)}
                />
              </Form.Field>
              <Form.Field>
                <label>周期</label>
                <Dropdown
                  selection
                  options={periodOptions}
                  value={editingPlan.period}
                  onChange={(_, { value }) => updatePlanField('period', value)}
                />
              </Form.Field>
              <Form.Field>
                <label>状态</label>
                <Dropdown
                  selection
                  options={statusOptions}
                  value={editingPlan.status}
                  onChange={(_, { value }) => updatePlanField('status', value)}
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths='equal'>
              <Form.Input
                label='套餐资源 Tokens'
                type='number'
                value={editingPlan.quota}
                onChange={(_, { value }) => updatePlanField('quota', value)}
              />
              <Form.Input
                label='套餐价格'
                type='number'
                value={editingPlan.price}
                onChange={(_, { value }) => updatePlanField('price', value)}
              />
              <Form.Input
                label='单 Token 单价'
                type='number'
                step='0.000001'
                value={editingPlan.unit_price}
                onChange={(_, { value }) => updatePlanField('unit_price', value)}
              />
            </Form.Group>
            <Form.Input
              label='预警阈值'
              value={editingPlan.alert_thresholds}
              onChange={(_, { value }) => updatePlanField('alert_thresholds', value)}
              placeholder='80,90,100'
            />
            <Form.TextArea
              label='套餐说明'
              rows={3}
              value={editingPlan.description}
              onChange={(_, { value }) => updatePlanField('description', value)}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setPlanModalOpen(false)}>取消</Button>
          <Button primary onClick={savePlan} loading={savingPlan}>
            保存
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
};

export default Operator;
